import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { PrivateScreenshot } from '@/components/funnel-audits/PrivateScreenshot';
import {
  SCREENSHOT_CATEGORIES, deleteFunnelAudit, getFunnelAudit,
  type FunnelAuditScreenshot,
} from '@/api/funnel-audits';
import {
  BONUS_LABELS, KYC_LABELS, PAYMENT_METHOD_LABELS, REGISTRATION_FIELD_LABELS,
  SCREENSHOT_LABELS, auditOfferLabel, formatDate, formatFlag, formatMinor,
} from '@/lib/funnel-audits';
import { useAuthStore } from '@/store/auth';

function listLabels(values: string[], labels: Record<string, string>): string {
  return values.length ? values.map((value) => labels[value] ?? value).join(', ') : '—';
}

export function FunnelAuditDetail() {
  const { id = '' } = useParams();
  const canEdit = useAuthStore((state) => state.canEdit());
  const canAccessOffers = useAuthStore((state) => state.hasSection('offers'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<FunnelAuditScreenshot | null>(null);
  const [removeError, setRemoveError] = useState('');
  const query = useQuery({ queryKey: ['funnel-audit', id], queryFn: () => getFunnelAudit(id) });
  const remove = useMutation({
    mutationFn: () => deleteFunnelAudit(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['funnel-audits'] });
      navigate('/funnel-audits');
    },
    onError: (error: Error) => setRemoveError(error.message || 'Failed to delete audit'),
  });

  if (query.isLoading) return <div className="py-16 text-center text-content-subtle">Loading audit…</div>;
  if (query.isError || !query.data) return <div className="card text-danger">Audit not found or unavailable.</div>;
  const { audit, screenshots, history } = query.data;
  const neverLinked = audit.offer_ref === `brand:${audit.brand_id}`;

  const rows: Array<[string, React.ReactNode]> = [
    ['Brand', auditOfferLabel(audit)],
    ['Connected offer', audit.offer_id
      ? audit.offer_partner
      : <span key="offer-state" className="text-content-subtle">{neverLinked ? 'Not connected yet' : 'Source offer deleted'}</span>],
    ['Funnel URL', <a key="url" href={audit.offer_url} target="_blank" rel="noreferrer" className="break-all text-primary hover:underline">{audit.offer_url}</a>],
    ['GEOs', audit.geos.join(', ')],
    ['Localized experience', formatFlag(audit.has_localization)],
    ['Registration complexity', audit.reg_complexity_score ? `${audit.reg_complexity_score} / 5` : '—'],
    ['Instant / one-click registration', formatFlag(audit.has_instant_one_click)],
    ['Required registration fields', listLabels(audit.registration_fields, REGISTRATION_FIELD_LABELS)],
    ['Minimum deposit', `${formatMinor(audit.min_deposit_amount)} ${audit.min_deposit_currency} ≈ $${formatMinor(audit.min_deposit_usd)}`],
    ['FX snapshot', `${audit.fx_rate.toFixed(6)} USD/${audit.min_deposit_currency} · ${formatDate(audit.fx_rate_at)}`],
    ['Payment methods', listLabels(audit.payment_methods, PAYMENT_METHOD_LABELS)],
    ['Crash games', formatFlag(audit.has_crash_games)],
    ['Welcome bonus', audit.bonus_type ? BONUS_LABELS[audit.bonus_type] : '—'],
    ['KYC policy', audit.kyc_policy ? KYC_LABELS[audit.kyc_policy] : '—'],
    ['Notes', audit.notes || '—'],
    ['Auditor', `${audit.created_by_name} (${audit.created_by_email})`],
    ['Created', formatDate(audit.created_at)],
    ['Last edited', formatDate(audit.updated_at)],
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div><h1 className="text-xl font-semibold text-content">{auditOfferLabel(audit)} — {audit.geos.join(', ')}</h1><p className="mt-1 text-sm text-content-subtle">Funnel audit from {formatDate(audit.created_at)}</p></div>
        <div className="ml-auto flex gap-2">
          <Link to="/funnel-audits"><Button variant="secondary">Back</Button></Link>
          {canEdit && canAccessOffers && !audit.offer_id && <Link to={`/offers?from_audit=${id}`}><Button>Create offer</Button></Link>}
          {canEdit && <Link to={`/funnel-audits/${id}/edit`}><Button variant="secondary">Edit</Button></Link>}
          {canEdit && <Button variant="danger" loading={remove.isPending} onClick={() => { if (window.confirm('Soft-delete this audit? Non-super-admin requests require approval.')) remove.mutate(); }}>Delete</Button>}
        </div>
      </div>
      {removeError && <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">{removeError}</div>}

      <dl className="card divide-y divide-border p-0">
        {rows.map(([label, value]) => <div key={label} className="grid gap-2 px-5 py-3 text-sm sm:grid-cols-3"><dt className="text-content-subtle">{label}</dt><dd className="text-content sm:col-span-2">{value}</dd></div>)}
      </dl>

      <section className="space-y-4">
        <h2 className="text-sm font-semibold text-content">Screenshots</h2>
        {screenshots.length === 0 && <p className="text-sm text-content-subtle">No screenshots attached.</p>}
        {SCREENSHOT_CATEGORIES.map((category) => {
          const categoryShots = screenshots.filter((shot) => shot.category === category);
          if (!categoryShots.length) return null;
          return <div key={category}><h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-subtle">{SCREENSHOT_LABELS[category]}</h3><div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{categoryShots.map((shot) => <button key={shot.id} type="button" onClick={() => setPreview(shot)} className="cursor-zoom-in text-left"><PrivateScreenshot id={shot.id} alt={shot.original_name} className="h-44 w-full rounded-lg border border-border object-cover shadow-sm hover:shadow-md" /><span className="mt-1 block truncate text-xs text-content-subtle">{shot.original_name}</span></button>)}</div></div>;
        })}
      </section>

      {history.length > 0 && <section><h2 className="mb-3 text-sm font-semibold text-content">Previous checks for this brand × {audit.geos.join(', ')}</h2><div className="card divide-y divide-border p-0">{history.map((item) => <Link key={item.id} to={`/funnel-audits/${item.id}`} className="flex flex-wrap items-center gap-4 px-5 py-3 text-sm hover:bg-surface-muted"><span className="font-medium text-primary">{formatDate(item.created_at)}</span><span className="text-content-muted">Min. deposit ${formatMinor(item.min_deposit_usd)}</span><span className="ml-auto text-content-subtle">{item.created_by_name}</span></Link>)}</div></section>}

      <Modal isOpen={preview != null} onClose={() => setPreview(null)} title={preview ? `${SCREENSHOT_LABELS[preview.category]} — ${preview.original_name}` : 'Screenshot'} size="full">
        {preview && <div className="flex h-full items-center justify-center bg-surface-sunken"><PrivateScreenshot id={preview.id} alt={preview.original_name} className="max-h-full max-w-full object-contain" /></div>}
      </Modal>
    </div>
  );
}
